import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchDepartments,
  deleteDepartment,
  setDepartmentEdit,
  clearDepartmentEdit,
} from '../../../../redux/actions/masters/department/department/departmentAction';
import dayjs from 'dayjs';
import { showConfirm, showInfo } from '../../../../utills/sweetAlert';
import SearchIcon from '@mui/icons-material/Search';

const cardStyle = {
  background: 'transparent',
  border: 'none',
  boxShadow: 'none',
  padding: 0,
  minWidth: 0,
  marginBottom: 0,
  fontFamily: 'Poppins, sans-serif',
};
const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 18,
};
const titleStyle = {
  fontWeight: 700,
  fontSize: 18,
  color: '#111',
  letterSpacing: 0.7,
};
const newBtnStyle = {
  background: 'linear-gradient(135deg, #00C853 0%, #43A047 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '10px 26px',
  fontWeight: 700,
  fontSize: 16,
  cursor: 'pointer',
  marginRight: 12,
  letterSpacing: 0.5,
  transition: 'all 0.3s ease',
  boxShadow: '0 2px 8px rgba(0, 200, 83, 0.3)',
};
const searchContainer = {
  position: 'relative',
  minWidth: 200,
  marginLeft: 8,
};
const searchInput = {
  width: 200,
  padding: '10px 36px 10px 38px',
  border: '1.5px solid #C8E6C9',
  borderRadius: 8,
  fontSize: 16,
  color: '#1B5E20',
  background: '#fff',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border 0.3s',
};
const searchIconStyle = {
  position: 'absolute',
  left: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  color: '#00C853',
  fontSize: 20,
  pointerEvents: 'none',
};
const actionStyle = {
  cursor: 'pointer',
  marginRight: 10,
  background: 'none',
  border: 'none',
  borderRadius: 6,
  padding: 4,
  color: '#757575',
  transition: 'all 0.3s ease',
};

const DepartmentTable = ({ onNew, departments, departmentGroups }) => {
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.department);
  const [search, setSearch] = useState('');
  const [hoveredAction, setHoveredAction] = useState({});

  const getDepartmentGroupName = (code) => {
    const group = departmentGroups.find(g => g.department_group_code === code);
    return group ? group.department_group_name : '';
  };

  const handleEdit = async (department) => {
    await showInfo('Edit Department', `Editing department: ${department.department_name}`);
    dispatch(setDepartmentEdit(department));
  };

  const handleDelete = async (id, name) => {
    const confirmed = await showConfirm('Trash Department', `Are you sure you want to trash department: ${name}?`, 'Trash', 'Cancel');
    if (confirmed) {
      dispatch(deleteDepartment(id));
    }
  };

  const handleNew = () => {
    dispatch(clearDepartmentEdit());
    if (typeof onNew === 'function') onNew();
  };

  const filteredDepartments = departments.filter(department =>
    department.department_name && department.department_name.toLowerCase().includes(search.toLowerCase()) && department.trash === 0
  );

  const columns = [
    {
      name: 'Department Code',
      selector: row => row.department_code,
      sortable: true,
    },
    {
      name: 'Department Name',
      selector: row => row.department_name,
      sortable: true,
      width: '200px',

    },
    {
      name: 'Department Group Name',
      selector: row => getDepartmentGroupName(row.department_group_code),
      sortable: true,
      width: '200px',

    },
    {
      name: 'Production Dept',
      selector: row => row.production_department ? 'Yes' : 'No',
      sortable: true,
      width: '150px',
      cell: row => (
        <span style={{ 
          color: row.production_department ? '#00C853' : '#757575', 
          fontWeight: 600,
          fontSize: '13px'
        }}>
          {row.production_department ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      name: 'Outward Dept',
      selector: row => row.outward_department ? 'Yes' : 'No',
      sortable: true,
      width: '150px',
      cell: row => (
        <span style={{ 
          color: row.outward_department ? '#00C853' : '#757575', 
          fontWeight: 600,
          fontSize: '13px'
        }}>
          {row.outward_department ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      name: 'Inward Dept',
      selector: row => row.inward_department ? 'Yes' : 'No',
      sortable: true,
      width: '150px',
      cell: row => (
        <span style={{ 
          color: row.inward_department ? '#00C853' : '#757575', 
          fontWeight: 600,
          fontSize: '13px'
        }}>
          {row.inward_department ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      name: 'Accepted Loss %',
      selector: row => row.accepted_loss_percentage || '-',
      sortable: true,
      width: '150px',
      cell: row => (
        <span style={{ 
          color: row.accepted_loss_percentage ? '#1B5E20' : '#757575', 
          fontWeight: 600,
          fontSize: '13px'
        }}>
          {row.accepted_loss_percentage || '-'}
        </span>
      ),
    },
    {
      name: 'Status',
      selector: row => row.status,
      sortable: true,
      width: '110px',
      cell: row => (
        <span style={{ color: row.status === 'Active' ? '#00C853' : '#d32f2f', fontWeight: 600 }}>{row.status}</span>
      ),
    },
    {
      name: 'Actions',
      cell: row => (
        <>
          <button
            style={actionStyle}
            onClick={() => handleEdit(row)}
            title="Edit"
            onMouseEnter={() => setHoveredAction({ id: row.id, type: 'edit' })}
            onMouseLeave={() => setHoveredAction({})}
          >
            <EditIcon fontSize="small" />
            {hoveredAction.id === row.id && hoveredAction.type === 'edit' && (
              <span style={{ marginLeft: 4, color: '#1B5E20', fontWeight: 600 }}>Edit</span>
            )}
          </button>
          <button
            style={actionStyle}
            onClick={() => handleDelete(row.department_id, row.department_name)}
            title="Trash"
            onMouseEnter={() => setHoveredAction({ id: row.department_id, type: 'delete' })}
            onMouseLeave={() => setHoveredAction({})}
          >
            <DeleteOutlineIcon fontSize="small" />
            {hoveredAction.id === row.department_id && hoveredAction.type === 'delete' && (
              <span style={{ marginLeft: 4, color: '#d32f2f', fontWeight: 600 }}>Trash</span>
            )}
          </button>
        </>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: '140px',
    },
  ];

  // Helper to check if error is a handled code error
  const isHandledCodeError = error && (
    error.includes('409') ||
    error.toLowerCase().includes('already exists') ||
    error.toLowerCase().includes('cannot be reused')
  );

  return (
    <div style={cardStyle}>
      <style>{`
        .department-table ::-webkit-scrollbar {
          height: 6px;
          background: #f2f2f2;
        }
        .department-table ::-webkit-scrollbar-thumb {
          background: #d3d3d3;
          border-radius: 4px;
        }
        .department-table {
          scrollbar-color: #d3d3d3 #f2f2f2;
          scrollbar-width: thin;
        }
      `}</style>
      <div style={headerStyle}>
        <span style={titleStyle}>Department List</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button style={newBtnStyle} onClick={handleNew}>+ New</button>
          <div style={searchContainer}>
            <input
              style={searchInput}
              type="text"
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <SearchIcon style={{ ...searchIconStyle, color: '#00C853' }} />
          </div>
        </div>
      </div>
      <DataTable
        columns={columns}
        data={filteredDepartments}
        pagination
        highlightOnHover
        striped
        progressPending={loading}
        noDataComponent={<div style={{ color: '#888', fontSize: 16 }}>No Departments found.</div>}
        customStyles={{
          headRow: { style: { background: '#E8F5E9', fontWeight: 700 } },
          rows: { style: { minHeight: 48 } },
        }}
        className="department-table"
      />
      {error && !isHandledCodeError && <div style={{ color: 'red', fontSize: 15, marginTop: 8 }}>{error}</div>}
    </div>
  );
};

export default DepartmentTable; 